import axios, { type AxiosInstance, type AxiosRequestConfig, AxiosError } from 'axios';
import qs from 'qs';
import { env } from '@/lib/env';
import { sessionHelper } from '@/auth/sessionHelper';

// Single axios instance for the whole app. Auth header is injected lazily per
// request so token refreshes are picked up without recreating the instance.
//
// Mirrors the Tenant.Web RequestWrapper pattern: 401 → refresh token → retry once;
// 403 → redirect to /unauthorized; everything else surfaces to the caller.

let refreshInFlight: Promise<void> | null = null;

/**
 * Thrown when the session is unrecoverable (no refresh token or refresh
 * itself failed). The interceptor uses this to (a) redirect the user to
 * /login synchronously and (b) surface a friendly message in any consumer
 * catch block that briefly renders before the navigation completes.
 */
export class SessionExpiredError extends Error {
  constructor() {
    super('Your session expired — please sign in again.');
    this.name = 'SessionExpiredError';
  }
}

async function refreshAccessToken(): Promise<void> {
  const refresh = sessionHelper.getRefreshToken();
  if (!refresh) {
    throw new SessionExpiredError();
  }

  const body = qs.stringify({
    client_id: env.oauthClientId,
    client_secret: env.oauthClientSecret,
    refresh_token: refresh,
    grant_type: 'refresh_token',
  });

  const res = await axios.post(`${env.apiUrl}/connect/token`, body, {
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
  });

  if (res.data?.access_token) {
    sessionHelper.saveToken(res.data.access_token);
    if (res.data.refresh_token) sessionHelper.saveRefreshToken(res.data.refresh_token);
  }
}

// ── PascalCase → camelCase normalization ──────────────────────────────────────
// The .NET API serializes responses with PascalCase property names
// (IsSuccess, Data, Items, DisplayName, …). All TypeScript DTOs in this app
// use idiomatic camelCase. Rather than litter each call site with mappers, we
// transform the entire response payload once here so every consumer sees a
// camelCase object — for refused requests (4xx/5xx) as well as successes.
//
// We skip strings, numbers, dates, and primitive wrappers — those don't have
// keys to rewrite. Arrays are mapped element-wise. Objects (including nested
// ones) get their keys lowercased on the first character. This matches
// System.Text.Json's default camelCase translation, so the resulting shape is
// what a System.Text.Json-configured server would have produced anyway.

function pascalToCamelKey(k: string): string {
  if (!k) return k;
  // Already camelCase or all-lowercase → leave alone.
  if (k[0] === k[0]?.toLowerCase()) return k;
  // ALL_CAPS keys (e.g. acronyms) — leave alone.
  if (k === k.toUpperCase()) return k;
  return k[0].toLowerCase() + k.slice(1);
}

function camelizeDeep(value: unknown): unknown {
  if (value == null) return value;
  if (Array.isArray(value)) return value.map(camelizeDeep);
  if (typeof value !== 'object') return value;
  // Don't touch Dates / TypedArrays / etc.
  if (value instanceof Date) return value;
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
    out[pascalToCamelKey(k)] = camelizeDeep(v);
  }
  return out;
}

/**
 * Camelizes a JSON object body in place on the response. Strings, blobs, etc. pass through: a Blob/ArrayBuffer IS
 * typeof 'object' and has no own keys, so without the instanceof checks a file download came back as {}.
 */
function normalizeBody(res: { data?: unknown } | undefined): void {
  if (res?.data && typeof res.data === 'object' && !(res.data instanceof Blob) && !(res.data instanceof ArrayBuffer)) {
    res.data = camelizeDeep(res.data);
  }
}

function buildClient(): AxiosInstance {
  const instance = axios.create({
    baseURL: env.apiUrl,
    paramsSerializer: { serialize: (params) => qs.stringify(params) },
  });

  instance.interceptors.request.use((config) => {
    const token = sessionHelper.getToken();
    if (token) {
      config.headers = config.headers ?? {};
      // axios v1 headers can be AxiosHeaders or plain object; either accepts indexer.
      (config.headers as Record<string, string>).Authorization = `Bearer ${token}`;
    }
    return config;
  });

  instance.interceptors.response.use(
    (res) => {
      normalizeBody(res);
      return res;
    },
    async (err: AxiosError) => {
      const originalRequest = err.config as (AxiosRequestConfig & { _retry?: boolean }) | undefined;
      const status = err.response?.status;

      if (status === 401 && originalRequest && !originalRequest._retry) {
        originalRequest._retry = true;
        try {
          if (!refreshInFlight) refreshInFlight = refreshAccessToken();
          await refreshInFlight;
          refreshInFlight = null;
          return instance(originalRequest);
        } catch (refreshErr) {
          refreshInFlight = null;
          sessionHelper.clear();
          // Force re-login. Avoid hard reload during dev to preserve console history.
          //
          // Stamp the path the user was on so LoginPage can drop them
          // back where they were. The `?session=expired` query string
          // signals LoginPage to render a friendly "Session expired —
          // sign in to continue" notice instead of the prior cold,
          // empty-form first-visit experience.
          if (window.location.pathname !== '/login') {
            sessionHelper.saveReturnTo(
              window.location.pathname + window.location.search,
            );
            window.location.assign('/login?session=expired');
          }
          // Always surface a SessionExpiredError so consumer catch blocks
          // (e.g. AgentRegistryStore) render a friendly message rather than
          // the technical "No refresh token available" string during the
          // brief moment before the location.assign completes.
          return Promise.reject(
            refreshErr instanceof SessionExpiredError ? refreshErr : new SessionExpiredError(),
          );
        }
      }

      // A refused request's body is the same OperationResult envelope (`Message`, `Data`) or ProblemDetails a success
      // carries, so it gets the same camelCase: a 409's current record and a 400's field problems reach the caller.
      normalizeBody(err.response);
      return Promise.reject(err);
    }
  );

  return instance;
}

export const apiClient = buildClient();

// ── Convenience wrappers (typed) ──────────────────────────────────────────────

export async function apiGet<T>(url: string, params?: unknown, config?: AxiosRequestConfig): Promise<T> {
  const res = await apiClient.get<T>(url, { ...config, params });
  return res.data;
}

export async function apiPost<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
  const res = await apiClient.post<T>(url, data, config);
  return res.data;
}

export async function apiPut<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
  const res = await apiClient.put<T>(url, data, config);
  return res.data;
}

export async function apiDelete<T>(url: string, params?: unknown): Promise<T> {
  const res = await apiClient.delete<T>(url, { params });
  return res.data;
}

type RefusedBody = { message?: string; title?: string; detail?: string };

/**
 * A failed request in the server's own words: the envelope's message (camelized above), else ProblemDetails' detail or
 * title, else the status it answered with, else why the call did not reach it. Promoted from scenarioIntelligence.ts.
 */
export function refusalWords(err: unknown): string {
  const e = err as AxiosError<RefusedBody>;
  const body = e?.response?.data && typeof e.response.data === 'object' ? e.response.data : undefined;
  if (body?.message) return body.message;
  if (body?.detail || body?.title) return body.detail || body.title!;
  if (e?.response?.status) return `The server answered ${e.response.status}.`;
  return (err as Error)?.message || 'Could not reach the server.';
}
